import * as React from "react";

// --- UI Primitives ---
import { Button, ButtonProps } from "@/components/_organisms/tiptap/tiptap-ui-primitive/button";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
// --- Lib ---
import { type Editor } from "@tiptap/react";

export type LineHeightType = {
  label: string;
  value: string;
};

export const lineHeightOptions: LineHeightType[] = [
  { label: "Single", value: "1" },
  { label: "1.15", value: "1.15" },
  { label: "1.5", value: "1.5" },
  { label: "Double", value: "2" },
  { label: "2.5", value: "2.5" },
  { label: "3", value: "3" },
];

export interface LineHeightButtonProps extends Omit<ButtonProps, "type"> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor;
  /**
   * The line height value to apply.
   */
  lineHeight: string;
  /**
   * The display text for the button.
   */
  text: string;
  /**
   * Whether the button should be hidden when unavailable.
   * @default false
   */
  hideWhenUnavailable?: boolean;
}

export function canSetLineHeight(editor: Editor | null, lineHeight: string): boolean {
  if (!editor) return false;
  // Check if the editor can set line height
  try {
    return editor.can().setLineHeight(lineHeight);
  } catch {
    return false;
  }
}

export function isLineHeightActive(editor: Editor | null, lineHeight: string): boolean {
  if (!editor) return false;
  // Check if the current selection has the specific line height
  const { selection } = editor.state;
  const { from, to } = selection;

  let hasLineHeight = false;
  editor.state.doc.nodesBetween(from, to, (node) => {
    if (node.attrs.lineHeight === lineHeight) {
      hasLineHeight = true;
      return false; // Stop traversing
    }
  });

  return hasLineHeight;
}

export const LineHeightButton = React.forwardRef<HTMLButtonElement, LineHeightButtonProps>(
  ({ editor: providedEditor, lineHeight, text, hideWhenUnavailable = false, ...props }, ref) => {
    const editor = useTiptapEditor(providedEditor);

    const canToggle = React.useMemo(() => canSetLineHeight(editor, lineHeight), [editor, lineHeight]);
    const isActive = React.useMemo(() => isLineHeightActive(editor, lineHeight), [editor, lineHeight]);

    const handleClick = React.useCallback(() => {
      if (editor && canToggle) {
        if (isActive) {
          editor.commands.unsetLineHeight();
        } else {
          editor.commands.setLineHeight(lineHeight);
        }
      }
    }, [editor, canToggle, lineHeight, isActive]);

    if (hideWhenUnavailable && !canToggle) {
      return null;
    }

    return (
      <Button
        type="button"
        data-style="ghost"
        data-active-state={isActive ? "on" : "off"}
        role="button"
        aria-label={text}
        aria-pressed={isActive}
        tooltip={text}
        {...props}
        onClick={handleClick}
        ref={ref}
      >
        <>{text && <span className="tiptap-button-text">{text}</span>}</>
      </Button>
    );
  },
);

LineHeightButton.displayName = "LineHeightButton";

export default LineHeightButton;
